import WaitingDots from 'components/WaitingDots';
import { Invoice } from 'models/Invoice';
import React, { useEffect, useState } from 'react';
import { Document, Page } from "react-pdf";
import { pdfDocumentOptions } from "pdfWorker";
import { getInvoiceExport } from 'services/AccountApi';
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { useAccountTranslation } from '../translations';
import { getThemeUrl, useTheme } from 'theme';

interface Props {
    invoice: Invoice;
    accountId: string;
}

export const InvoiceRowAccount: React.FC<Props> = ({ invoice, accountId }) => {
    const { t, intlLocale } = useAccountTranslation();
    const theme = useTheme();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [previewError, setPreviewError] = useState("");
    const [fileURL, setFileURL] = useState("");
    const [pdfPages, setPdfPages] = useState(0);
    const [pdfWidth, setPdfWidth] = useState(() =>
        Math.min(800, Math.max(240, window.innerWidth - 96))
    );
    const downloadName = `${invoice.GetDisplayName() || invoice.id}.pdf`;
    const outgoing = invoice.supplier === accountId;
    const directionLabel = t(outgoing ? "outgoingInvoice" : "incomingInvoice");

    useEffect(() => {
        const resize = () => setPdfWidth(Math.min(800, Math.max(240, window.innerWidth - 96)));
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
    }, []);

    useEffect(() => () => {
        if (fileURL) URL.revokeObjectURL(fileURL);
    }, [fileURL]);

    const getInvoiceData = async () => {
        setPreviewOpen(true);
        setPreviewError("");
        setPdfPages(0);
        if (fileURL) return;

        setLoading(true);
        try {
            const pdf = await getInvoiceExport(invoice.id);
            const byteCharacters = atob(pdf);
            const byteNumbers = new Uint8Array(byteCharacters.length);
            for (let index = 0; index < byteCharacters.length; index++) {
                byteNumbers[index] = byteCharacters.charCodeAt(index);
            }
            setFileURL(URL.createObjectURL(new Blob([byteNumbers], { type: "application/pdf" })));
        } catch {
            setPreviewError(t("pdfLoadError"));
        } finally {
            setLoading(false);
        }
    };

    if (invoice === undefined) {
        return <WaitingDots />
    }

    return (<>
        <tr>
            <td>
                <span className="d-flex align-items-center gap-2" style={{ width: '140px', whiteSpace: "nowrap", textOverflow: 'ellipsis' }}>
                    <i
                        className={`mdi ${outgoing ? "mdi-arrow-down-bold text-success" : "mdi-arrow-up-bold text-danger"} flex-shrink-0`}
                        aria-label={directionLabel}
                        title={directionLabel}
                        role="img"
                    />
                    {invoice.GetDisplayName() || <i>{t("noName")}</i>}
                </span>
            </td>
            <td>
                <span style={{ whiteSpace: "nowrap" }}>
                    {new Intl.DateTimeFormat(intlLocale).format(invoice.GetCreatedDate())}
                </span>
            </td>
            <td>
                <span style={{ whiteSpace: "nowrap" }}>
                    &euro;{invoice.CalcGrandTotal().toFixed(2)}
                </span>
            </td>
            <td>
                <span style={{ whiteSpace: "nowrap" }}>
                    {invoice.quote ?
                        <button type="button" disabled className="btn btn-sm btn-outline-primary">
                            {t("quote")}
                        </button>
                        :
                        <>
                            {invoice.draft ?
                                <button type="button" disabled className="btn btn-sm btn-outline-primary">
                                    {t("draft")}
                                </button>
                                :
                                <>
                                    {invoice.IsPaid() ?
                                        <button type="button" disabled className="btn btn-sm btn-outline-success">
                                            {t("paid")}
                                        </button>
                                        :

                                        <button type="button" disabled className="btn btn-sm btn-outline-danger">
                                            {t("open")}
                                        </button>}
                                </>
                            }
                        </>
                    }
                </span>
            </td>
            <td>
                <span style={{ whiteSpace: "nowrap" }}>
                    <button onClick={getInvoiceData} type="button" className="btn btn-sm w-xs btn-primary">
                        <i className="mdi mdi-file-eye-outline" /> {t("openDocument")}
                    </button>
                </span>
            </td>

            {
                invoice.customer === accountId && !invoice.draft && !invoice.IsPaid() && invoice.token?.trim() !== "" ?
                    <td>
                        <span style={{ whiteSpace: "nowrap" }}>
                            <button type="button" className="btn btn-sm btn-success waves-effect waves-light"
                                onClick={() => {
                                    window.open(getThemeUrl(theme, "pay", `/?${btoa(`${invoice.id}*${invoice.token}`)}`))

                                }}
                            >
                                {t("clickToPay")}
                            </button>
                        </span>
                    </td>
                    :
                    <>
                    </>
            }
        </tr>

        <Modal isOpen={previewOpen} toggle={() => setPreviewOpen(false)} size="xl" scrollable>
            <ModalHeader toggle={() => setPreviewOpen(false)}>
                {invoice.GetDisplayName() || t("noName")}
            </ModalHeader>
            <ModalBody>
                {loading && <div className="text-center py-5"><Spinner /></div>}
                {previewError && <Alert color="danger">{previewError}</Alert>}
                {fileURL && <div className="overflow-auto">
                    <Document
                        file={fileURL}
                        options={pdfDocumentOptions}
                        onLoadSuccess={({ numPages }) => setPdfPages(numPages)}
                        loading={<div className="text-center py-5"><Spinner /></div>}
                        error={<Alert color="danger">{t("pdfLoadError")}</Alert>}
                    >
                        {Array.from({ length: pdfPages }, (_, index) =>
                            <div key={index} className="mb-3 mx-auto shadow-sm" style={{ width: pdfWidth }}>
                                <Page
                                    pageNumber={index + 1}
                                    width={pdfWidth}
                                    renderAnnotationLayer={false}
                                    renderTextLayer={false}
                                />
                            </div>
                        )}
                    </Document>
                </div>}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={() => setPreviewOpen(false)}>
                    {t("close")}
                </Button>
                {fileURL && <Button
                    tag="a"
                    color="primary"
                    href={fileURL}
                    download={downloadName}
                >
                    <i className="mdi mdi-download me-1" />{t("download")}
                </Button>}
            </ModalFooter>
        </Modal>
    </>);
}

export default InvoiceRowAccount;
